import React, { useState } from 'react';
import PropTypes from 'prop-types';
import BaseSelect, { components } from 'react-select';
import * as Styled from './styled';

// this section is adjusting the composition of some of the sub-components
// in `react-switch`, prop validation is done within that component so some
// meeting this project's prop linting rules here would be duplicated effort

/* eslint-disable react/jsx-props-no-spreading, react/prop-types */

// use custom icon on trigger that opens the select drop-down
function DropdownIndicatorVal(props) {
  return (
    <components.DropdownIndicator {...props}>
      <Styled.DropdownIcon />
    </components.DropdownIndicator>
  );
}

function SelectOption(props) {
  const {
    className,
    selectProps,
    children,
    isFocused,
    isDisabled,
    isSelected,
  } = props;
  const { focusVisible } = selectProps;

  if (selectProps.inputId === 'language-select') {
    return (
      <components.Option
        {...props}
        className={`${className} ${isFocused ? 'focus-visible' : ''}`.trim()}
      >
        {children}
        {isDisabled && <Styled.CheckIcon />}
      </components.Option>
    );
  }

  return (
    <components.Option
      {...props}
      className={
        // because `react-select` uses class names instead of the :focus
        // pseudo element for focus styling the focus-visible shim doesn't
        // work with it, this className handling adds that compatibility
        `${className} ${focusVisible ? 'focus-visible' : ''}`.trim()
      }
    >
      <Styled.InnerOptionWrapper>
        {children}
        {isSelected && <Styled.CheckIcon />}
      </Styled.InnerOptionWrapper>
    </components.Option>
  );
}

const caretComponents = {
  DropdownIndicator: DropdownIndicatorVal,
  Option: SelectOption,
};

const noCaretComponents = {
  DropdownIndicator: () => null,
  Option: SelectOption,
};

/* eslint-enable react/jsx-props-no-spreading, react/prop-types */

function Select({
  value,
  onChange,
  options,
  ariaLabel,
  classNamePrefix,
  className,
  ariaLabelledBy,
  ariaLiveMessages,
  inputId,
  name,
}) {
  const [focusVisible, setFocusVisible] = useState(false);
  const isLanguageSelector = ariaLabel === 'Language';

  return (
    <BaseSelect
      value={value}
      onChange={onChange}
      options={options}
      isSearchable={false}
      components={isLanguageSelector ? noCaretComponents : caretComponents}
      onMenuClose={() => setFocusVisible(false)}
      onKeyDown={e => {
        if (['ArrowUp', 'ArrowDown'].includes(e.key)) setFocusVisible(true);
      }}
      focusVisible={focusVisible}
      aria-label={ariaLabel}
      classNamePrefix={classNamePrefix}
      className={className}
      aria-labelledby={ariaLabelledBy}
      ariaLiveMessages={ariaLiveMessages}
      inputId={inputId}
      name={name}
    />
  );
}

Select.propTypes = {
  value: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  }),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      // Add additional properties as required here.
    })
  ).isRequired,
  ariaLabel: PropTypes.string,
  classNamePrefix: PropTypes.string,
  className: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  ariaLiveMessages: PropTypes.shape({}),
  inputId: PropTypes.string,
  name: PropTypes.string,
};

Select.defaultProps = {
  value: null,
  ariaLabel: undefined,
  classNamePrefix: 'trimet',
  className: '',
  ariaLabelledBy: null,
  ariaLiveMessages: null,
  inputId: null,
  name: null,
};

export default Select;
